import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import { CheckCircle, XCircle, Clock, ArrowRight, Package, Calendar, DollarSign } from "lucide-react";
import api from "../../utils/api";
import { motion } from "framer-motion";

const PackagePaymentStatus = () => {
    const { packageId, status: urlStatus, lang } = useParams();
    const currentLang = lang || 'en';
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get("session_id");

    const [loading, setLoading] = useState(true);
    const [paymentData, setPaymentData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                // استدعاء الـ API للتحقق من حالة البكج
                const response = await api.get(`/payments/package/${packageId}/status`);
                setPaymentData(response.data.data);
            } catch (err) {
                console.error("Error fetching payment status:", err);
                setError("Could not verify payment status. Please contact support.");
            } finally {
                setLoading(false);
            }
        };

        if (packageId) {
            checkStatus();
        } else {
            navigate(`/${currentLang}`);
        }
    }, [packageId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <h2 className="text-xl font-semibold text-gray-700">Verifying your payment...</h2>
                <p className="text-gray-500 mt-2">Please wait while we confirm your purchase.</p>
            </div>
        );
    }

    const isSuccess = urlStatus === "success" && paymentData?.is_purchased;

    return (
        <div className="min-h-screen bg-gray-50 py-20 px-6">
            <div className="max-w-3xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl shadow-xl overflow-hidden"
                >
                    {/* Header Section */}
                    <div className={`py-12 px-8 text-center ${isSuccess ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="inline-flex items-center justify-center mb-6">
                            {isSuccess ? (
                                <div className="bg-green-100 p-4 rounded-full">
                                    <CheckCircle className="w-16 h-16 text-green-600" />
                                </div>
                            ) : (
                                <div className="bg-red-100 p-4 rounded-full">
                                    <XCircle className="w-16 h-16 text-red-600" />
                                </div>
                            )}
                        </div>

                        <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${isSuccess ? 'text-green-900' : 'text-red-900'}`}>
                            {isSuccess ? "Payment Successful!" : "Payment Failed or Canceled"}
                        </h1>
                        <p className="text-lg text-gray-600 max-w-md mx-auto">
                            {isSuccess
                                ? "Thank you for your purchase. Your immigration journey just got easier!"
                                : "We couldn't process your payment. If this was a mistake, you can try again."}
                        </p>
                    </div>

                    {/* Details Section */}
                    <div className="p-8 md:p-12">
                        {isSuccess && paymentData && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                                <div className="flex items-center gap-4 p-4 border border-gray-100 rounded-2xl bg-gray-50/50">
                                    <div className="bg-blue-100 p-3 rounded-xl">
                                        <Package className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Package ID</p>
                                        <p className="font-semibold text-gray-900">#{paymentData.package_id}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 p-4 border border-gray-100 rounded-2xl bg-gray-50/50">
                                    <div className="bg-green-100 p-3 rounded-xl">
                                        <DollarSign className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Amount Paid</p>
                                        <p className="font-semibold text-gray-900">${paymentData.amount_paid}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 p-4 border border-gray-100 rounded-2xl bg-gray-50/50 md:col-span-2">
                                    <div className="bg-purple-100 p-3 rounded-xl">
                                        <Calendar className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">Purchased At</p>
                                        <p className="font-semibold text-gray-900">
                                            {new Date(paymentData.purchased_at).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isSuccess && error && (
                            <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-8 border border-red-100 flex items-center gap-3">
                                <Clock className="w-5 h-5" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {isSuccess ? (
                                <Link
                                    to="/profile/client"
                                    className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-2xl transition-all shadow-lg hover:shadow-xl group"
                                >
                                    Go to My Portal
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            ) : (
                                <button
                                    onClick={() => navigate(-1)}
                                    className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white font-bold py-4 px-8 rounded-2xl transition-all shadow-lg"
                                >
                                    Try Again
                                </button>
                            )}

                            <LanguageLink
                                to="/"
                                className="inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 px-8 rounded-2xl transition-all"
                            >
                                Back to Home
                            </LanguageLink>
                        </div>
                    </div>
                </motion.div>

                {/* Footer simple trust note */}
                <p className="text-center text-gray-500 mt-8 text-sm">
                    If you have any questions, please contact our support team at <span className="text-blue-600 font-medium">support@citizenhub.com</span>
                </p>
            </div>
        </div>
    );
};

export default PackagePaymentStatus;
